/**
 * Country Extended Information Database
 * Contains major cities, capital, region, timezone, currency, and other relevant data
 * for enhanced country page content and SEO.
 */

export interface CountryExtendedInfo {
  country_alpha_code: string;
  capital: string;
  majorCities: string[];
  region: string;
  subregion: string;
  timezone: string;
  currency: {
    code: string;
    name: string;
    symbol: string;
  };
  languages: string[];
  population?: number;
  area?: number; // in km²
  description?: string;
}

export const COUNTRY_INFO: Record<string, CountryExtendedInfo> = {
  // Europe
  "AL": {
    country_alpha_code: "AL",
    capital: "Tirana",
    majorCities: ["Tirana", "Durrës", "Vlorë", "Elbasan", "Shkodër"],
    region: "Europe",
    subregion: "Southern Europe",
    timezone: "UTC+1",
    currency: { code: "ALL", name: "Albanian Lek", symbol: "L" },
    languages: ["Albanian"],
    population: 2877797,
    area: 28748,
  },
  "AT": {
    country_alpha_code: "AT",
    capital: "Vienna",
    majorCities: ["Vienna", "Graz", "Linz", "Salzburg", "Innsbruck"],
    region: "Europe",
    subregion: "Western Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["German"],
    population: 9006398,
    area: 83871,
  },
  "BE": {
    country_alpha_code: "BE",
    capital: "Brussels",
    majorCities: ["Brussels", "Antwerp", "Ghent", "Charleroi", "Liège"],
    region: "Europe",
    subregion: "Western Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Dutch", "French", "German"],
    population: 11589623,
    area: 30528,
  },
  "BG": {
    country_alpha_code: "BG",
    capital: "Sofia",
    majorCities: ["Sofia", "Plovdiv", "Varna", "Burgas", "Ruse"],
    region: "Europe",
    subregion: "Eastern Europe",
    timezone: "UTC+2",
    currency: { code: "BGN", name: "Bulgarian Lev", symbol: "лв" },
    languages: ["Bulgarian"],
    population: 6948445,
    area: 110879,
  },
  "BA": {
    country_alpha_code: "BA",
    capital: "Sarajevo",
    majorCities: ["Sarajevo", "Banja Luka", "Tuzla", "Zenica", "Mostar"],
    region: "Europe",
    subregion: "Southern Europe",
    timezone: "UTC+1",
    currency: { code: "BAM", name: "Bosnia-Herzegovina Convertible Mark", symbol: "KM" },
    languages: ["Bosnian", "Croatian", "Serbian"],
    population: 3280819,
    area: 51197,
  },
  "BY": {
    country_alpha_code: "BY",
    capital: "Minsk",
    majorCities: ["Minsk", "Gomel", "Mogilev", "Vitebsk", "Grodno"],
    region: "Europe",
    subregion: "Eastern Europe",
    timezone: "UTC+3",
    currency: { code: "BYN", name: "Belarusian Ruble", symbol: "Br" },
    languages: ["Belarusian", "Russian"],
    population: 9449323,
    area: 207600,
  },
  "HR": {
    country_alpha_code: "HR",
    capital: "Zagreb",
    majorCities: ["Zagreb", "Split", "Rijeka", "Osijek", "Zadar"],
    region: "Europe",
    subregion: "Southern Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Croatian"],
    population: 4105267,
    area: 56594,
  },
  "CZ": {
    country_alpha_code: "CZ",
    capital: "Prague",
    majorCities: ["Prague", "Brno", "Ostrava", "Plzen", "Liberec"],
    region: "Europe",
    subregion: "Central Europe",
    timezone: "UTC+1",
    currency: { code: "CZK", name: "Czech Koruna", symbol: "Kč" },
    languages: ["Czech"],
    population: 10708981,
    area: 78867,
  },
  "DK": {
    country_alpha_code: "DK",
    capital: "Copenhagen",
    majorCities: ["Copenhagen", "Aarhus", "Odense", "Aalborg", "Frederiksberg"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+1",
    currency: { code: "DKK", name: "Danish Krone", symbol: "kr" },
    languages: ["Danish"],
    population: 5818553,
    area: 43094,
  },
  "EE": {
    country_alpha_code: "EE",
    capital: "Tallinn",
    majorCities: ["Tallinn", "Tartu", "Narva", "Pärnu", "Kohtla-Järve"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+2",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Estonian"],
    population: 1326535,
    area: 45228,
  },
  "FI": {
    country_alpha_code: "FI",
    capital: "Helsinki",
    majorCities: ["Helsinki", "Espoo", "Tampere", "Vantaa", "Oulu"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+2",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Finnish", "Swedish"],
    population: 5540720,
    area: 338424,
  },
  "FR": {
    country_alpha_code: "FR",
    capital: "Paris",
    majorCities: ["Paris", "Marseille", "Lyon", "Toulouse", "Nice"],
    region: "Europe",
    subregion: "Western Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["French"],
    population: 67390000,
    area: 551695,
  },
  "DE": {
    country_alpha_code: "DE",
    capital: "Berlin",
    majorCities: ["Berlin", "Hamburg", "Munich", "Cologne", "Frankfurt"],
    region: "Europe",
    subregion: "Western Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["German"],
    population: 83783942,
    area: 357114,
  },
  "GR": {
    country_alpha_code: "GR",
    capital: "Athens",
    majorCities: ["Athens", "Thessaloniki", "Patras", "Heraklion", "Larissa"],
    region: "Europe",
    subregion: "Southern Europe",
    timezone: "UTC+2",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Greek"],
    population: 10423054,
    area: 131990,
  },
  "HU": {
    country_alpha_code: "HU",
    capital: "Budapest",
    majorCities: ["Budapest", "Debrecen", "Szeged", "Miskolc", "Pécs"],
    region: "Europe",
    subregion: "Central Europe",
    timezone: "UTC+1",
    currency: { code: "HUF", name: "Hungarian Forint", symbol: "Ft" },
    languages: ["Hungarian"],
    population: 9660351,
    area: 93028,
  },
  "IS": {
    country_alpha_code: "IS",
    capital: "Reykjavik",
    majorCities: ["Reykjavik", "Kópavogur", "Hafnarfjörður", "Akureyri", "Reykjanesbær"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+0",
    currency: { code: "ISK", name: "Icelandic Króna", symbol: "kr" },
    languages: ["Icelandic"],
    population: 341243,
    area: 103000,
  },
  "IE": {
    country_alpha_code: "IE",
    capital: "Dublin",
    majorCities: ["Dublin", "Cork", "Limerick", "Galway", "Waterford"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+0",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Irish", "English"],
    population: 4937786,
    area: 70273,
  },
  "IT": {
    country_alpha_code: "IT",
    capital: "Rome",
    majorCities: ["Rome", "Milan", "Naples", "Turin", "Palermo"],
    region: "Europe",
    subregion: "Southern Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Italian"],
    population: 60461826,
    area: 301340,
  },
  "LV": {
    country_alpha_code: "LV",
    capital: "Riga",
    majorCities: ["Riga", "Daugavpils", "Liepāja", "Jelgava", "Jūrmala"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+2",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Latvian"],
    population: 1886198,
    area: 64589,
  },
  "LT": {
    country_alpha_code: "LT",
    capital: "Vilnius",
    majorCities: ["Vilnius", "Kaunas", "Klaipėda", "Šiauliai", "Panevėžys"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+2",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Lithuanian"],
    population: 2722289,
    area: 65300,
  },
  "LU": {
    country_alpha_code: "LU",
    capital: "Luxembourg City",
    majorCities: ["Luxembourg City", "Esch-sur-Alzette", "Dudelange", "Schifflange", "Bettembourg"],
    region: "Europe",
    subregion: "Western Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Luxembourgish", "French", "German"],
    population: 625978,
    area: 2586,
  },
  "MD": {
    country_alpha_code: "MD",
    capital: "Chișinău",
    majorCities: ["Chișinău", "Tiraspol", "Bălți", "Bender", "Rîbnița"],
    region: "Europe",
    subregion: "Eastern Europe",
    timezone: "UTC+2",
    currency: { code: "MDL", name: "Moldovan Leu", symbol: "L" },
    languages: ["Romanian"],
    population: 2657637,
    area: 33851,
  },
  "NL": {
    country_alpha_code: "NL",
    capital: "Amsterdam",
    majorCities: ["Amsterdam", "Rotterdam", "The Hague", "Utrecht", "Eindhoven"],
    region: "Europe",
    subregion: "Western Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Dutch"],
    population: 17134872,
    area: 41850,
  },
  "NO": {
    country_alpha_code: "NO",
    capital: "Oslo",
    majorCities: ["Oslo", "Bergen", "Trondheim", "Stavanger", "Drammen"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+1",
    currency: { code: "NOK", name: "Norwegian Krone", symbol: "kr" },
    languages: ["Norwegian"],
    population: 5421241,
    area: 323802,
  },
  "PL": {
    country_alpha_code: "PL",
    capital: "Warsaw",
    majorCities: ["Warsaw", "Kraków", "Łódź", "Wrocław", "Poznań"],
    region: "Europe",
    subregion: "Central Europe",
    timezone: "UTC+1",
    currency: { code: "PLN", name: "Polish Złoty", symbol: "zł" },
    languages: ["Polish"],
    population: 37846611,
    area: 312696,
  },
  "PT": {
    country_alpha_code: "PT",
    capital: "Lisbon",
    majorCities: ["Lisbon", "Porto", "Vila Nova de Gaia", "Amadora", "Braga"],
    region: "Europe",
    subregion: "Southern Europe",
    timezone: "UTC+0",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Portuguese"],
    population: 10196709,
    area: 92090,
  },
  "RO": {
    country_alpha_code: "RO",
    capital: "Bucharest",
    majorCities: ["Bucharest", "Cluj-Napoca", "Timișoara", "Iași", "Constanța"],
    region: "Europe",
    subregion: "Eastern Europe",
    timezone: "UTC+2",
    currency: { code: "RON", name: "Romanian Leu", symbol: "lei" },
    languages: ["Romanian"],
    population: 19237691,
    area: 238397,
  },
  "RU": {
    country_alpha_code: "RU",
    capital: "Moscow",
    majorCities: ["Moscow", "Saint Petersburg", "Novosibirsk", "Yekaterinburg", "Kazan"],
    region: "Europe",
    subregion: "Eastern Europe",
    timezone: "UTC+3",
    currency: { code: "RUB", name: "Russian Ruble", symbol: "₽" },
    languages: ["Russian"],
    population: 145934462,
    area: 17098242,
  },
  "RS": {
    country_alpha_code: "RS",
    capital: "Belgrade",
    majorCities: ["Belgrade", "Novi Sad", "Niš", "Kragujevac", "Subotica"],
    region: "Europe",
    subregion: "Southern Europe",
    timezone: "UTC+1",
    currency: { code: "RSD", name: "Serbian Dinar", symbol: "дин" },
    languages: ["Serbian"],
    population: 6982084,
    area: 88361,
  },
  "SK": {
    country_alpha_code: "SK",
    capital: "Bratislava",
    majorCities: ["Bratislava", "Košice", "Prešov", "Žilina", "Nitra"],
    region: "Europe",
    subregion: "Central Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Slovak"],
    population: 5459642,
    area: 49035,
  },
  "SI": {
    country_alpha_code: "SI",
    capital: "Ljubljana",
    majorCities: ["Ljubljana", "Maribor", "Celje", "Kranj", "Velenje"],
    region: "Europe",
    subregion: "Southern Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Slovene"],
    population: 2078938,
    area: 20273,
  },
  "ES": {
    country_alpha_code: "ES",
    capital: "Madrid",
    majorCities: ["Madrid", "Barcelona", "Valencia", "Seville", "Zaragoza"],
    region: "Europe",
    subregion: "Southern Europe",
    timezone: "UTC+1",
    currency: { code: "EUR", name: "Euro", symbol: "€" },
    languages: ["Spanish"],
    population: 46754778,
    area: 505990,
  },
  "SE": {
    country_alpha_code: "SE",
    capital: "Stockholm",
    majorCities: ["Stockholm", "Gothenburg", "Malmö", "Uppsala", "Västerås"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+1",
    currency: { code: "SEK", name: "Swedish Krona", symbol: "kr" },
    languages: ["Swedish"],
    population: 10099265,
    area: 450295,
  },
  "CH": {
    country_alpha_code: "CH",
    capital: "Bern",
    majorCities: ["Zürich", "Geneva", "Basel", "Lausanne", "Bern"],
    region: "Europe",
    subregion: "Western Europe",
    timezone: "UTC+1",
    currency: { code: "CHF", name: "Swiss Franc", symbol: "CHF" },
    languages: ["German", "French", "Italian", "Romansh"],
    population: 8654622,
    area: 41284,
  },
  "UA": {
    country_alpha_code: "UA",
    capital: "Kyiv",
    majorCities: ["Kyiv", "Kharkiv", "Odesa", "Dnipro", "Donetsk"],
    region: "Europe",
    subregion: "Eastern Europe",
    timezone: "UTC+2",
    currency: { code: "UAH", name: "Ukrainian Hryvnia", symbol: "₴" },
    languages: ["Ukrainian"],
    population: 43733762,
    area: 603500,
  },
  "GB": {
    country_alpha_code: "GB",
    capital: "London",
    majorCities: ["London", "Birmingham", "Manchester", "Glasgow", "Liverpool"],
    region: "Europe",
    subregion: "Northern Europe",
    timezone: "UTC+0",
    currency: { code: "GBP", name: "British Pound", symbol: "£" },
    languages: ["English"],
    population: 67886011,
    area: 242495,
  },
  
  // Asia
  "AZ": {
    country_alpha_code: "AZ",
    capital: "Baku",
    majorCities: ["Baku", "Ganja", "Sumqayit", "Mingachevir", "Qaraçuxur"],
    region: "Asia",
    subregion: "Western Asia",
    timezone: "UTC+4",
    currency: { code: "AZN", name: "Azerbaijani Manat", symbol: "₼" },
    languages: ["Azerbaijani"],
    population: 10139177,
    area: 86600,
  },
  "BD": {
    country_alpha_code: "BD",
    capital: "Dhaka",
    majorCities: ["Dhaka", "Chittagong", "Khulna", "Rajshahi", "Sylhet"],
    region: "Asia",
    subregion: "Southern Asia",
    timezone: "UTC+6",
    currency: { code: "BDT", name: "Bangladeshi Taka", symbol: "৳" },
    languages: ["Bengali"],
    population: 164689383,
    area: 147570,
  },
  "CN": {
    country_alpha_code: "CN",
    capital: "Beijing",
    majorCities: ["Shanghai", "Beijing", "Shenzhen", "Guangzhou", "Chengdu"],
    region: "Asia",
    subregion: "Eastern Asia",
    timezone: "UTC+8",
    currency: { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
    languages: ["Mandarin Chinese"],
    population: 1402112000,
    area: 9596961,
  },
  "GE": {
    country_alpha_code: "GE",
    capital: "Tbilisi",
    majorCities: ["Tbilisi", "Batumi", "Kutaisi", "Rustavi", "Zugdidi"],
    region: "Asia",
    subregion: "Western Asia",
    timezone: "UTC+4",
    currency: { code: "GEL", name: "Georgian Lari", symbol: "₾" },
    languages: ["Georgian"],
    population: 3989167,
    area: 69700,
  },
  "HK": {
    country_alpha_code: "HK",
    capital: "Hong Kong",
    majorCities: ["Victoria", "Kowloon", "Tsuen Wan", "Sha Tin", "Tuen Mun"],
    region: "Asia",
    subregion: "Eastern Asia",
    timezone: "UTC+8",
    currency: { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$" },
    languages: ["Chinese", "English"],
    population: 7496981,
    area: 1106,
  },
  "IN": {
    country_alpha_code: "IN",
    capital: "New Delhi",
    majorCities: ["Mumbai", "Delhi", "Bangalore", "Hyderabad", "Chennai"],
    region: "Asia",
    subregion: "Southern Asia",
    timezone: "UTC+5:30",
    currency: { code: "INR", name: "Indian Rupee", symbol: "₹" },
    languages: ["Hindi", "English"],
    population: 1380004385,
    area: 3287263,
  },
  "ID": {
    country_alpha_code: "ID",
    capital: "Jakarta",
    majorCities: ["Jakarta", "Surabaya", "Bandung", "Medan", "Semarang"],
    region: "Asia",
    subregion: "South-Eastern Asia",
    timezone: "UTC+7",
    currency: { code: "IDR", name: "Indonesian Rupiah", symbol: "Rp" },
    languages: ["Indonesian"],
    population: 273523615,
    area: 1904569,
  },
  "IR": {
    country_alpha_code: "IR",
    capital: "Tehran",
    majorCities: ["Tehran", "Mashhad", "Isfahan", "Karaj", "Tabriz"],
    region: "Asia",
    subregion: "Southern Asia",
    timezone: "UTC+3:30",
    currency: { code: "IRR", name: "Iranian Rial", symbol: "﷼" },
    languages: ["Persian"],
    population: 83992949,
    area: 1648195,
  },
  "IL": {
    country_alpha_code: "IL",
    capital: "Jerusalem",
    majorCities: ["Jerusalem", "Tel Aviv", "Haifa", "Rishon LeZion", "Petah Tikva"],
    region: "Asia",
    subregion: "Western Asia",
    timezone: "UTC+2",
    currency: { code: "ILS", name: "Israeli New Shekel", symbol: "₪" },
    languages: ["Hebrew", "Arabic"],
    population: 8655535,
    area: 20770,
  },
  "JP": {
    country_alpha_code: "JP",
    capital: "Tokyo",
    majorCities: ["Tokyo", "Yokohama", "Osaka", "Nagoya", "Sapporo"],
    region: "Asia",
    subregion: "Eastern Asia",
    timezone: "UTC+9",
    currency: { code: "JPY", name: "Japanese Yen", symbol: "¥" },
    languages: ["Japanese"],
    population: 126476461,
    area: 377975,
  },
  "KZ": {
    country_alpha_code: "KZ",
    capital: "Nur-Sultan",
    majorCities: ["Almaty", "Nur-Sultan", "Shymkent", "Karaganda", "Aktobe"],
    region: "Asia",
    subregion: "Central Asia",
    timezone: "UTC+6",
    currency: { code: "KZT", name: "Kazakhstani Tenge", symbol: "₸" },
    languages: ["Kazakh", "Russian"],
    population: 18776707,
    area: 2724900,
  },
  "KR": {
    country_alpha_code: "KR",
    capital: "Seoul",
    majorCities: ["Seoul", "Busan", "Incheon", "Daegu", "Daejeon"],
    region: "Asia",
    subregion: "Eastern Asia",
    timezone: "UTC+9",
    currency: { code: "KRW", name: "South Korean Won", symbol: "₩" },
    languages: ["Korean"],
    population: 51269185,
    area: 100210,
  },
  "MY": {
    country_alpha_code: "MY",
    capital: "Kuala Lumpur",
    majorCities: ["Kuala Lumpur", "George Town", "Ipoh", "Johor Bahru", "Kota Kinabalu"],
    region: "Asia",
    subregion: "South-Eastern Asia",
    timezone: "UTC+8",
    currency: { code: "MYR", name: "Malaysian Ringgit", symbol: "RM" },
    languages: ["Malay"],
    population: 32365999,
    area: 330803,
  },
  "PK": {
    country_alpha_code: "PK",
    capital: "Islamabad",
    majorCities: ["Karachi", "Lahore", "Faisalabad", "Rawalpindi", "Gujranwala"],
    region: "Asia",
    subregion: "Southern Asia",
    timezone: "UTC+5",
    currency: { code: "PKR", name: "Pakistani Rupee", symbol: "₨" },
    languages: ["Urdu", "English"],
    population: 220892340,
    area: 881912,
  },
  "PH": {
    country_alpha_code: "PH",
    capital: "Manila",
    majorCities: ["Manila", "Quezon City", "Davao", "Cebu City", "Zamboanga"],
    region: "Asia",
    subregion: "South-Eastern Asia",
    timezone: "UTC+8",
    currency: { code: "PHP", name: "Philippine Peso", symbol: "₱" },
    languages: ["Filipino", "English"],
    population: 109581078,
    area: 300000,
  },
  "QA": {
    country_alpha_code: "QA",
    capital: "Doha",
    majorCities: ["Doha", "Al Rayyan", "Al Wakrah", "Al Khor", "Umm Salal"],
    region: "Asia",
    subregion: "Western Asia",
    timezone: "UTC+3",
    currency: { code: "QAR", name: "Qatari Riyal", symbol: "﷼" },
    languages: ["Arabic"],
    population: 2881053,
    area: 11586,
  },
  "SA": {
    country_alpha_code: "SA",
    capital: "Riyadh",
    majorCities: ["Riyadh", "Jeddah", "Mecca", "Medina", "Dammam"],
    region: "Asia",
    subregion: "Western Asia",
    timezone: "UTC+3",
    currency: { code: "SAR", name: "Saudi Riyal", symbol: "﷼" },
    languages: ["Arabic"],
    population: 34813871,
    area: 2149690,
  },
  "SG": {
    country_alpha_code: "SG",
    capital: "Singapore",
    majorCities: ["Singapore", "Woodlands", "Jurong West", "Tampines", "Pasir Ris"],
    region: "Asia",
    subregion: "South-Eastern Asia",
    timezone: "UTC+8",
    currency: { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
    languages: ["English", "Malay", "Mandarin", "Tamil"],
    population: 5850342,
    area: 719,
  },
  "TW": {
    country_alpha_code: "TW",
    capital: "Taipei",
    majorCities: ["Taipei", "Kaohsiung", "Taichung", "Tainan", "Banqiao"],
    region: "Asia",
    subregion: "Eastern Asia",
    timezone: "UTC+8",
    currency: { code: "TWD", name: "New Taiwan Dollar", symbol: "NT$" },
    languages: ["Mandarin Chinese"],
    population: 23816775,
    area: 36193,
  },
  "TH": {
    country_alpha_code: "TH",
    capital: "Bangkok",
    majorCities: ["Bangkok", "Nonthaburi", "Nakhon Ratchasima", "Chiang Mai", "Pattaya"],
    region: "Asia",
    subregion: "South-Eastern Asia",
    timezone: "UTC+7",
    currency: { code: "THB", name: "Thai Baht", symbol: "฿" },
    languages: ["Thai"],
    population: 69799978,
    area: 513120,
  },
  "TR": {
    country_alpha_code: "TR",
    capital: "Ankara",
    majorCities: ["Istanbul", "Ankara", "Izmir", "Bursa", "Antalya"],
    region: "Asia",
    subregion: "Western Asia",
    timezone: "UTC+3",
    currency: { code: "TRY", name: "Turkish Lira", symbol: "₺" },
    languages: ["Turkish"],
    population: 84339067,
    area: 783562,
  },
  "AE": {
    country_alpha_code: "AE",
    capital: "Abu Dhabi",
    majorCities: ["Dubai", "Abu Dhabi", "Sharjah", "Al Ain", "Ajman"],
    region: "Asia",
    subregion: "Western Asia",
    timezone: "UTC+4",
    currency: { code: "AED", name: "UAE Dirham", symbol: "د.إ" },
    languages: ["Arabic"],
    population: 9890402,
    area: 83600,
  },
  "UZ": {
    country_alpha_code: "UZ",
    capital: "Tashkent",
    majorCities: ["Tashkent", "Namangan", "Samarkand", "Andijan", "Bukhara"],
    region: "Asia",
    subregion: "Central Asia",
    timezone: "UTC+5",
    currency: { code: "UZS", name: "Uzbekistani Som", symbol: "сўм" },
    languages: ["Uzbek"],
    population: 33469203,
    area: 448978,
  },
  "VN": {
    country_alpha_code: "VN",
    capital: "Hanoi",
    majorCities: ["Ho Chi Minh City", "Hanoi", "Da Nang", "Hai Phong", "Can Tho"],
    region: "Asia",
    subregion: "South-Eastern Asia",
    timezone: "UTC+7",
    currency: { code: "VND", name: "Vietnamese Dong", symbol: "₫" },
    languages: ["Vietnamese"],
    population: 97338579,
    area: 331212,
  },
  "AM": {
    country_alpha_code: "AM",
    capital: "Yerevan",
    majorCities: ["Yerevan", "Gyumri", "Vanadzor", "Vagharshapat", "Hrazdan"],
    region: "Asia",
    subregion: "Western Asia",
    timezone: "UTC+4",
    currency: { code: "AMD", name: "Armenian Dram", symbol: "֏" },
    languages: ["Armenian"],
    population: 2963243,
    area: 29743,
  },
  
  // Americas
  "AR": {
    country_alpha_code: "AR",
    capital: "Buenos Aires",
    majorCities: ["Buenos Aires", "Córdoba", "Rosario", "Mendoza", "Tucumán"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-3",
    currency: { code: "ARS", name: "Argentine Peso", symbol: "$" },
    languages: ["Spanish"],
    population: 45195774,
    area: 2780400,
  },
  "BO": {
    country_alpha_code: "BO",
    capital: "Sucre",
    majorCities: ["Santa Cruz", "El Alto", "La Paz", "Cochabamba", "Sucre"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-4",
    currency: { code: "BOB", name: "Bolivian Boliviano", symbol: "Bs." },
    languages: ["Spanish"],
    population: 11673021,
    area: 1098581,
  },
  "BR": {
    country_alpha_code: "BR",
    capital: "Brasília",
    majorCities: ["São Paulo", "Rio de Janeiro", "Brasília", "Salvador", "Fortaleza"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-3",
    currency: { code: "BRL", name: "Brazilian Real", symbol: "R$" },
    languages: ["Portuguese"],
    population: 212559417,
    area: 8515767,
  },
  "CA": {
    country_alpha_code: "CA",
    capital: "Ottawa",
    majorCities: ["Toronto", "Montreal", "Vancouver", "Calgary", "Edmonton"],
    region: "Americas",
    subregion: "North America",
    timezone: "UTC-5",
    currency: { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
    languages: ["English", "French"],
    population: 37742154,
    area: 9984670,
  },
  "CL": {
    country_alpha_code: "CL",
    capital: "Santiago",
    majorCities: ["Santiago", "Puente Alto", "Antofagasta", "Viña del Mar", "Valparaíso"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-4",
    currency: { code: "CLP", name: "Chilean Peso", symbol: "$" },
    languages: ["Spanish"],
    population: 19116201,
    area: 756102,
  },
  "CO": {
    country_alpha_code: "CO",
    capital: "Bogotá",
    majorCities: ["Bogotá", "Medellín", "Cali", "Barranquilla", "Cartagena"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-5",
    currency: { code: "COP", name: "Colombian Peso", symbol: "$" },
    languages: ["Spanish"],
    population: 50882891,
    area: 1141748,
  },
  "CR": {
    country_alpha_code: "CR",
    capital: "San José",
    majorCities: ["San José", "Limón", "Alajuela", "Heredia", "Cartago"],
    region: "Americas",
    subregion: "Central America",
    timezone: "UTC-6",
    currency: { code: "CRC", name: "Costa Rican Colón", symbol: "₡" },
    languages: ["Spanish"],
    population: 5094118,
    area: 51100,
  },
  "CU": {
    country_alpha_code: "CU",
    capital: "Havana",
    majorCities: ["Havana", "Santiago de Cuba", "Camagüey", "Holguín", "Santa Clara"],
    region: "Americas",
    subregion: "Caribbean",
    timezone: "UTC-5",
    currency: { code: "CUP", name: "Cuban Peso", symbol: "$" },
    languages: ["Spanish"],
    population: 11326616,
    area: 109884,
  },
  "DO": {
    country_alpha_code: "DO",
    capital: "Santo Domingo",
    majorCities: ["Santo Domingo", "Santiago", "Santo Domingo Este", "San Pedro de Macorís", "La Romana"],
    region: "Americas",
    subregion: "Caribbean",
    timezone: "UTC-4",
    currency: { code: "DOP", name: "Dominican Peso", symbol: "RD$" },
    languages: ["Spanish"],
    population: 10847910,
    area: 48671,
  },
  "EC": {
    country_alpha_code: "EC",
    capital: "Quito",
    majorCities: ["Guayaquil", "Quito", "Cuenca", "Santo Domingo", "Machala"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-5",
    currency: { code: "USD", name: "United States Dollar", symbol: "$" },
    languages: ["Spanish"],
    population: 17643054,
    area: 283561,
  },
  "SV": {
    country_alpha_code: "SV",
    capital: "San Salvador",
    majorCities: ["San Salvador", "Santa Ana", "San Miguel", "Mejicanos", "Santa Tecla"],
    region: "Americas",
    subregion: "Central America",
    timezone: "UTC-6",
    currency: { code: "USD", name: "United States Dollar", symbol: "$" },
    languages: ["Spanish"],
    population: 6486205,
    area: 21041,
  },
  "GT": {
    country_alpha_code: "GT",
    capital: "Guatemala City",
    majorCities: ["Guatemala City", "Mixco", "Villa Nueva", "Petapa", "San Juan Sacatepéquez"],
    region: "Americas",
    subregion: "Central America",
    timezone: "UTC-6",
    currency: { code: "GTQ", name: "Guatemalan Quetzal", symbol: "Q" },
    languages: ["Spanish"],
    population: 17915568,
    area: 108889,
  },
  "HN": {
    country_alpha_code: "HN",
    capital: "Tegucigalpa",
    majorCities: ["Tegucigalpa", "San Pedro Sula", "Choloma", "La Ceiba", "El Progreso"],
    region: "Americas",
    subregion: "Central America",
    timezone: "UTC-6",
    currency: { code: "HNL", name: "Honduran Lempira", symbol: "L" },
    languages: ["Spanish"],
    population: 9904607,
    area: 112492,
  },
  "JM": {
    country_alpha_code: "JM",
    capital: "Kingston",
    majorCities: ["Kingston", "Spanish Town", "Portmore", "Montego Bay", "May Pen"],
    region: "Americas",
    subregion: "Caribbean",
    timezone: "UTC-5",
    currency: { code: "JMD", name: "Jamaican Dollar", symbol: "J$" },
    languages: ["English"],
    population: 2961167,
    area: 10991,
  },
  "MX": {
    country_alpha_code: "MX",
    capital: "Mexico City",
    majorCities: ["Mexico City", "Guadalajara", "Monterrey", "Puebla", "Tijuana"],
    region: "Americas",
    subregion: "North America",
    timezone: "UTC-6",
    currency: { code: "MXN", name: "Mexican Peso", symbol: "$" },
    languages: ["Spanish"],
    population: 128932753,
    area: 1964375,
  },
  "NI": {
    country_alpha_code: "NI",
    capital: "Managua",
    majorCities: ["Managua", "León", "Masaya", "Matagalpa", "Chinandega"],
    region: "Americas",
    subregion: "Central America",
    timezone: "UTC-6",
    currency: { code: "NIO", name: "Nicaraguan Córdoba", symbol: "C$" },
    languages: ["Spanish"],
    population: 6624554,
    area: 130373,
  },
  "PA": {
    country_alpha_code: "PA",
    capital: "Panama City",
    majorCities: ["Panama City", "San Miguelito", "Juan Díaz", "David", "Arraiján"],
    region: "Americas",
    subregion: "Central America",
    timezone: "UTC-5",
    currency: { code: "PAB", name: "Panamanian Balboa", symbol: "B/." },
    languages: ["Spanish"],
    population: 4314767,
    area: 75417,
  },
  "PY": {
    country_alpha_code: "PY",
    capital: "Asunción",
    majorCities: ["Asunción", "Ciudad del Este", "San Lorenzo", "Luque", "Capiatá"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-4",
    currency: { code: "PYG", name: "Paraguayan Guaraní", symbol: "₲" },
    languages: ["Spanish", "Guaraní"],
    population: 7132538,
    area: 406752,
  },
  "PE": {
    country_alpha_code: "PE",
    capital: "Lima",
    majorCities: ["Lima", "Arequipa", "Callao", "Trujillo", "Chiclayo"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-5",
    currency: { code: "PEN", name: "Peruvian Sol", symbol: "S/." },
    languages: ["Spanish"],
    population: 32971854,
    area: 1285216,
  },
  "PR": {
    country_alpha_code: "PR",
    capital: "San Juan",
    majorCities: ["San Juan", "Bayamón", "Carolina", "Ponce", "Caguas"],
    region: "Americas",
    subregion: "Caribbean",
    timezone: "UTC-4",
    currency: { code: "USD", name: "United States Dollar", symbol: "$" },
    languages: ["Spanish", "English"],
    population: 3193694,
    area: 9104,
  },
  "US": {
    country_alpha_code: "US",
    capital: "Washington, D.C.",
    majorCities: ["New York", "Los Angeles", "Chicago", "Houston", "Phoenix"],
    region: "Americas",
    subregion: "North America",
    timezone: "UTC-5",
    currency: { code: "USD", name: "United States Dollar", symbol: "$" },
    languages: ["English"],
    population: 331002651,
    area: 9833520,
  },
  "UY": {
    country_alpha_code: "UY",
    capital: "Montevideo",
    majorCities: ["Montevideo", "Salto", "Ciudad de la Costa", "Paysandú", "Las Piedras"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-3",
    currency: { code: "UYU", name: "Uruguayan Peso", symbol: "$U" },
    languages: ["Spanish"],
    population: 3473730,
    area: 176215,
  },
  "VE": {
    country_alpha_code: "VE",
    capital: "Caracas",
    majorCities: ["Caracas", "Maracaibo", "Valencia", "Barquisimeto", "Maracay"],
    region: "Americas",
    subregion: "South America",
    timezone: "UTC-4",
    currency: { code: "VES", name: "Venezuelan Bolívar", symbol: "Bs." },
    languages: ["Spanish"],
    population: 28435940,
    area: 916445,
  },
  
  // Africa
  "DZ": {
    country_alpha_code: "DZ",
    capital: "Algiers",
    majorCities: ["Algiers", "Oran", "Constantine", "Annaba", "Blida"],
    region: "Africa",
    subregion: "Northern Africa",
    timezone: "UTC+1",
    currency: { code: "DZD", name: "Algerian Dinar", symbol: "د.ج" },
    languages: ["Arabic", "Berber"],
    population: 43851044,
    area: 2381741,
  },
  "AO": {
    country_alpha_code: "AO",
    capital: "Luanda",
    majorCities: ["Luanda", "Huambo", "Lobito", "Benguela", "Lubango"],
    region: "Africa",
    subregion: "Middle Africa",
    timezone: "UTC+1",
    currency: { code: "AOA", name: "Angolan Kwanza", symbol: "Kz" },
    languages: ["Portuguese"],
    population: 32866272,
    area: 1246700,
  },
  "CM": {
    country_alpha_code: "CM",
    capital: "Yaoundé",
    majorCities: ["Douala", "Yaoundé", "Garoua", "Bamenda", "Maroua"],
    region: "Africa",
    subregion: "Middle Africa",
    timezone: "UTC+1",
    currency: { code: "XAF", name: "Central African CFA Franc", symbol: "FCFA" },
    languages: ["French", "English"],
    population: 26545863,
    area: 475442,
  },
  "CG": {
    country_alpha_code: "CG",
    capital: "Brazzaville",
    majorCities: ["Brazzaville", "Pointe-Noire", "Dolisie", "Nkayi", "Impfondo"],
    region: "Africa",
    subregion: "Middle Africa",
    timezone: "UTC+1",
    currency: { code: "XAF", name: "Central African CFA Franc", symbol: "FCFA" },
    languages: ["French"],
    population: 5518087,
    area: 342000,
  },
  "CI": {
    country_alpha_code: "CI",
    capital: "Yamoussoukro",
    majorCities: ["Abidjan", "Bouaké", "Daloa", "Yamoussoukro", "Korhogo"],
    region: "Africa",
    subregion: "Western Africa",
    timezone: "UTC+0",
    currency: { code: "XOF", name: "West African CFA Franc", symbol: "CFA" },
    languages: ["French"],
    population: 26378274,
    area: 322463,
  },
  "EG": {
    country_alpha_code: "EG",
    capital: "Cairo",
    majorCities: ["Cairo", "Alexandria", "Giza", "Shubra El Kheima", "Port Said"],
    region: "Africa",
    subregion: "Northern Africa",
    timezone: "UTC+2",
    currency: { code: "EGP", name: "Egyptian Pound", symbol: "E£" },
    languages: ["Arabic"],
    population: 102334404,
    area: 1002450,
  },
  "ET": {
    country_alpha_code: "ET",
    capital: "Addis Ababa",
    majorCities: ["Addis Ababa", "Dire Dawa", "Mekelle", "Gondar", "Bahir Dar"],
    region: "Africa",
    subregion: "Eastern Africa",
    timezone: "UTC+3",
    currency: { code: "ETB", name: "Ethiopian Birr", symbol: "Br" },
    languages: ["Amharic"],
    population: 114963588,
    area: 1104300,
  },
  "GH": {
    country_alpha_code: "GH",
    capital: "Accra",
    majorCities: ["Accra", "Kumasi", "Tamale", "Takoradi", "Ashaiman"],
    region: "Africa",
    subregion: "Western Africa",
    timezone: "UTC+0",
    currency: { code: "GHS", name: "Ghanaian Cedi", symbol: "GH₵" },
    languages: ["English"],
    population: 31072940,
    area: 238533,
  },
  "KE": {
    country_alpha_code: "KE",
    capital: "Nairobi",
    majorCities: ["Nairobi", "Mombasa", "Kisumu", "Nakuru", "Eldoret"],
    region: "Africa",
    subregion: "Eastern Africa",
    timezone: "UTC+3",
    currency: { code: "KES", name: "Kenyan Shilling", symbol: "KSh" },
    languages: ["English", "Swahili"],
    population: 53771296,
    area: 580367,
  },
  "MG": {
    country_alpha_code: "MG",
    capital: "Antananarivo",
    majorCities: ["Antananarivo", "Toamasina", "Antsirabe", "Fianarantsoa", "Mahajanga"],
    region: "Africa",
    subregion: "Eastern Africa",
    timezone: "UTC+3",
    currency: { code: "MGA", name: "Malagasy Ariary", symbol: "Ar" },
    languages: ["Malagasy", "French"],
    population: 27691018,
    area: 587041,
  },
  "MA": {
    country_alpha_code: "MA",
    capital: "Rabat",
    majorCities: ["Casablanca", "Fez", "Tangier", "Marrakech", "Salé"],
    region: "Africa",
    subregion: "Northern Africa",
    timezone: "UTC+1",
    currency: { code: "MAD", name: "Moroccan Dirham", symbol: "د.م." },
    languages: ["Arabic", "Berber"],
    population: 36910560,
    area: 446550,
  },
  "MZ": {
    country_alpha_code: "MZ",
    capital: "Maputo",
    majorCities: ["Maputo", "Matola", "Beira", "Nampula", "Chimoio"],
    region: "Africa",
    subregion: "Eastern Africa",
    timezone: "UTC+2",
    currency: { code: "MZN", name: "Mozambican Metical", symbol: "MT" },
    languages: ["Portuguese"],
    population: 31255435,
    area: 801590,
  },
  "NG": {
    country_alpha_code: "NG",
    capital: "Abuja",
    majorCities: ["Lagos", "Kano", "Ibadan", "Abuja", "Port Harcourt"],
    region: "Africa",
    subregion: "Western Africa",
    timezone: "UTC+1",
    currency: { code: "NGN", name: "Nigerian Naira", symbol: "₦" },
    languages: ["English"],
    population: 206139589,
    area: 923768,
  },
  "RW": {
    country_alpha_code: "RW",
    capital: "Kigali",
    majorCities: ["Kigali", "Butare", "Gitarama", "Ruhengeri", "Gisenyi"],
    region: "Africa",
    subregion: "Eastern Africa",
    timezone: "UTC+2",
    currency: { code: "RWF", name: "Rwandan Franc", symbol: "FRw" },
    languages: ["Kinyarwanda", "French", "English"],
    population: 12952218,
    area: 26338,
  },
  "SN": {
    country_alpha_code: "SN",
    capital: "Dakar",
    majorCities: ["Dakar", "Touba", "Thiès", "Rufisque", "Kaolack"],
    region: "Africa",
    subregion: "Western Africa",
    timezone: "UTC+0",
    currency: { code: "XOF", name: "West African CFA Franc", symbol: "CFA" },
    languages: ["French"],
    population: 16743927,
    area: 196722,
  },
  "SL": {
    country_alpha_code: "SL",
    capital: "Freetown",
    majorCities: ["Freetown", "Bo", "Kenema", "Makeni", "Koidu"],
    region: "Africa",
    subregion: "Western Africa",
    timezone: "UTC+0",
    currency: { code: "SLL", name: "Sierra Leonean Leone", symbol: "Le" },
    languages: ["English"],
    population: 7976983,
    area: 71740,
  },
  "ZA": {
    country_alpha_code: "ZA",
    capital: "Pretoria",
    majorCities: ["Johannesburg", "Cape Town", "Durban", "Pretoria", "Port Elizabeth"],
    region: "Africa",
    subregion: "Southern Africa",
    timezone: "UTC+2",
    currency: { code: "ZAR", name: "South African Rand", symbol: "R" },
    languages: ["Afrikaans", "English", "Zulu", "Xhosa"],
    population: 59308690,
    area: 1221037,
  },
  "TZ": {
    country_alpha_code: "TZ",
    capital: "Dodoma",
    majorCities: ["Dar es Salaam", "Mwanza", "Arusha", "Dodoma", "Mbeya"],
    region: "Africa",
    subregion: "Eastern Africa",
    timezone: "UTC+3",
    currency: { code: "TZS", name: "Tanzanian Shilling", symbol: "TSh" },
    languages: ["Swahili", "English"],
    population: 59734218,
    area: 945087,
  },
  "TN": {
    country_alpha_code: "TN",
    capital: "Tunis",
    majorCities: ["Tunis", "Sfax", "Sousse", "Kairouan", "Bizerte"],
    region: "Africa",
    subregion: "Northern Africa",
    timezone: "UTC+1",
    currency: { code: "TND", name: "Tunisian Dinar", symbol: "د.ت" },
    languages: ["Arabic"],
    population: 11818619,
    area: 163610,
  },
  "UG": {
    country_alpha_code: "UG",
    capital: "Kampala",
    majorCities: ["Kampala", "Gulu", "Lira", "Mbarara", "Jinja"],
    region: "Africa",
    subregion: "Eastern Africa",
    timezone: "UTC+3",
    currency: { code: "UGX", name: "Ugandan Shilling", symbol: "USh" },
    languages: ["English", "Swahili"],
    population: 45741007,
    area: 241550,
  },
  "ZM": {
    country_alpha_code: "ZM",
    capital: "Lusaka",
    majorCities: ["Lusaka", "Kitwe", "Ndola", "Kabwe", "Chingola"],
    region: "Africa",
    subregion: "Eastern Africa",
    timezone: "UTC+2",
    currency: { code: "ZMW", name: "Zambian Kwacha", symbol: "ZK" },
    languages: ["English"],
    population: 18383955,
    area: 752612,
  },
  "ZW": {
    country_alpha_code: "ZW",
    capital: "Harare",
    majorCities: ["Harare", "Bulawayo", "Chitungwiza", "Mutare", "Gweru"],
    region: "Africa",
    subregion: "Eastern Africa",
    timezone: "UTC+2",
    currency: { code: "ZWL", name: "Zimbabwean Dollar", symbol: "$" },
    languages: ["English", "Shona", "Ndebele"],
    population: 14862924,
    area: 390757,
  },
  
  // Oceania
  "AU": {
    country_alpha_code: "AU",
    capital: "Canberra",
    majorCities: ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide"],
    region: "Oceania",
    subregion: "Australia and New Zealand",
    timezone: "UTC+10",
    currency: { code: "AUD", name: "Australian Dollar", symbol: "A$" },
    languages: ["English"],
    population: 25499884,
    area: 7692024,
  },
  "NZ": {
    country_alpha_code: "NZ",
    capital: "Wellington",
    majorCities: ["Auckland", "Wellington", "Christchurch", "Hamilton", "Tauranga"],
    region: "Oceania",
    subregion: "Australia and New Zealand",
    timezone: "UTC+12",
    currency: { code: "NZD", name: "New Zealand Dollar", symbol: "NZ$" },
    languages: ["English", "Māori"],
    population: 4822233,
    area: 270467,
  },
};

/**
 * Get extended country information by ISO alpha-2 country code
 */
export function getCountryInfo(countryCode: string): CountryExtendedInfo | null {
  const upperCode = countryCode.toUpperCase();
  return COUNTRY_INFO[upperCode] || null;
}

/**
 * Get extended country information by country name
 */
export function getCountryInfoByName(countryName: string): CountryExtendedInfo | null {
  // Try direct lookup first (some entries might have name as key)
  const entries = Object.entries(COUNTRY_INFO);
  for (const [, info] of entries) {
    // For now, we only have alpha codes as keys
    // Future: could add name lookup
  }
  return null;
}

/**
 * Format population number with locale string
 */
export function formatPopulation(population: number | undefined): string {
  if (!population) return "N/A";
  if (population >= 1000000) {
    return `${(population / 1000000).toFixed(1)}M`;
  }
  if (population >= 1000) {
    return `${(population / 1000).toFixed(1)}K`;
  }
  return population.toLocaleString();
}

/**
 * Format area with km² suffix
 */
export function formatArea(area: number | undefined): string {
  if (!area) return "N/A";
  return `${area.toLocaleString()} km²`;
}
